import styles from '../styles/pages/Memberships.module.scss';
import AppointmentsIcon from '../assets/icons/appointments.svg'
import { numberToText } from '../utils/caseConverter';
import { Link, useSearchParams } from 'react-router-dom';
import { useUpdateUserBoutique, useUserProfile } from '../hooks/useUserProfile';
import { useMemberships } from '../hooks/useMemberships';
import Spinner from '../components/core/Spinner';
import { useBoutique } from '../hooks/useBoutiques';
import ArrowIcon from '../assets/icons/arrow-down.svg';
import { useEffect, useState } from 'react';
import LocationSearch from '../components/LocationSearch';
import ErrorScreen from '../components/core/ErrorMessages';
import { formatNumber } from '../utils/formatNumber';
import { Boutique } from '../types/boutiques';
import { useToast } from '../components/core/ToastManager';
import { useQueryClient } from '@tanstack/react-query';

const MembershipBenefits = [
  { title: 'Monthly Savings', copy: 'Save on monthly massages.', icon: AppointmentsIcon },
  { title: 'Roll-Over Unused Credits', copy: 'Save your unused massage credits for later.', icon: AppointmentsIcon },
  { title: 'Relax Anywhere, Anytime', copy: 'Enjoy your membership benefits at any NOW boutique from coast to coast.*', disclaimer: "*Additional charges may apply", icon: AppointmentsIcon },
  { title: 'Share the Benefits', copy: 'Gift two massage credits per year or extend your member rate to friends and family new to The NOW.', icon: AppointmentsIcon },
  { title: 'Extend the Relaxation', copy: 'Get 20% off in-store purchases on Nourish, Scent & Room by The NOW products.', icon: AppointmentsIcon },
  { title: 'Exclusive Perks, Events & Offers', copy: 'Access The Ritual Collective including premium perks from our favorite brands plus member-only wellness events.', icon: AppointmentsIcon },
];

export const Memberships = () => {
  const { data: userProfile, isLoading: isLoadingUserProfile, error: userProfileError } = useUserProfile();
  const [searchParams] = useSearchParams();
  const locationParam = searchParams.get('location');
  const locationId = locationParam !== 'null' && locationParam !== 'undefined' ? locationParam : undefined
  const [selectedBoutique, setSelectedBoutique] = useState<string>(locationId || userProfile?.data.boutiqueId || '');
  const { data: boutique, isLoading: isLoadingBoutique, error: boutiqueError } = useBoutique(selectedBoutique || userProfile?.data.boutiqueId || '');
  const { data: boutiqueMemberships, isLoading: isLoadingMemberships, error: membershipsError } = useMemberships(selectedBoutique || userProfile?.data.boutiqueId || '');
  const [showLocationSearch, setShowLocationSearch] = useState<boolean>(false);
  const isLocationPickerLocked = locationId && boutique?.data.name
  const { addToast } = useToast()
  const queryClient = useQueryClient()

  const updateBoutiqueMutation = useUpdateUserBoutique();

  const handleUserBoutiqueChange = async (boutique: string | Boutique) => {
    const boutiqueId = typeof boutique === 'string' ? boutique : boutique.id;
    try {
      await updateBoutiqueMutation.mutateAsync(boutiqueId);
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      queryClient.invalidateQueries({ queryKey: ['boutique', { boutique_id: userProfile?.data.boutiqueId }] });
      addToast('Boutique updated successfully', 'success');
    } catch (error) {
      addToast('Failed to update boutique. Please try again.', 'error');
    }
  };

  useEffect(() => {
    if (boutiqueError) {
      setShowLocationSearch(true)
    }
  }, [boutiqueError])

  if (isLoadingUserProfile || isLoadingMemberships || isLoadingBoutique) {
    return <div><Spinner /></div>;
  }

  if (userProfileError || membershipsError) return <ErrorScreen errors={[userProfileError ?? undefined, membershipsError ?? undefined, boutiqueError ?? undefined]} />
  const hasMemberships = boutiqueMemberships?.membershipGroups?.some(group => group.memberships.length > 0);

  return (
    <div className={styles.noMemberships}>
      <div className={styles.header}>
        <h3 className={styles.benefitTitle}>The Ritual Membership</h3>
        <p>Make massage a part of your monthly ritual. No long-term commitments, cancel any time with 30 days notice.</p>
      </div>
      <div className={styles.membershipPerks}>
        <h3>Membership Perks</h3>
        <div className={styles.membershipBenefits}>
        {MembershipBenefits.map((benefit, index) => (
          <div key={index} className={styles.membershipBenefit}>
            <img src={benefit.icon} alt="benefit icon" />
            <p><strong>{benefit.title}</strong></p>
            <p>{benefit.copy}</p>
            {benefit.disclaimer && (<p>{benefit.disclaimer}</p>)}
          </div>
        ))}
        </div>
      </div>

      <div className={styles.locationContainer}>
        <div className={styles.locationPickerContainer}>
          <h2>Select a Boutique:</h2>
          {/* <LocationPicker currentBoutique={boutique?.data.id || ''} onBoutiqueChange={handleBoutiqueChange} locked={!!isLocationPickerLocked} /> */}
        </div>
        <div className={`${styles.boutiqueCardLocation} ${isLocationPickerLocked ? 'locked' : ''}`} onClick={() => setShowLocationSearch(true)}>
          <h4>{boutique?.data.name}</h4>
          <p>{boutique?.data.address} {boutique?.data.address2}, {boutique?.data.city}, {boutique?.data.state} {boutique?.data.zip}</p>
          <img className={styles.arrowIcon} src={ArrowIcon} alt="arrow" />
        </div>
      </div>
      <div className={styles.selectMembership}>
        {hasMemberships && boutiqueMemberships ? (
          boutiqueMemberships.membershipGroups.map((membershipGroup) => (
            membershipGroup.memberships.length > 0 && (
              <div key={membershipGroup.id} className={styles.membershipColumn}>
                <h3>{membershipGroup.name} ({membershipGroup.memberships[0].durationInMinutes} min)</h3>
                {membershipGroup.memberships.map((membership) => (
                  <div
                    key={membership.zenotiId}
                    className={styles.membershipCard}
                  >
                    <div className={styles.membershipContentLeft}>
                      <p className={styles.membershipFrequency}>
                        {numberToText(membership.frequencyPerMonth)} {membership.durationInMinutes} minute {membership.frequencyPerMonth > 1 ? 'massages' : 'massage'}
                      </p>
                      <p className={membership.zeroDown && membership.futurePrice ? styles.perMonthCopy : ''}>per month</p>
                      {membership.zeroDown && membership.futurePrice && <p className={styles.zeroDownDisclaimer}>*Your first payment will be due on the date the boutique opens.</p>}
                      <Link to={`/checkout/${membership.boutique.id}/membership/${membership.zenotiId}`} className={`button`}>Choose</Link>
                    </div>
                    <div className={styles.membershipContentRight}>
                      <div className={styles.price}>
                        <div className={styles.membershipPrice}>${membership.zeroDown && membership.futurePrice ? `${formatNumber(membership.futurePrice)}*` : formatNumber(membership.price)}</div>
                        <div className={styles.nonMemberPrice}>per month</div>
                      </div>
                      {membership.memberSaving && <p className={styles.discount}>${formatNumber(membership.memberSaving)} SAVINGS</p>}
                    </div>
                  </div>
                ))}
              </div>
            )
          ))
        ) : (
          <p>No memberships found for selected location.</p>
        )}
      </div>
      {showLocationSearch && 
        <LocationSearch
          currentBoutique={boutique?.data || null}
          setBoutique={boutiqueError ? handleUserBoutiqueChange : (boutique) => setSelectedBoutique(boutique.id)}
          onClose={() => setShowLocationSearch(false)}
          selectLocationButtonCopy='Select'
          applyPhaseFilter
        />
      }
    </div>
  );
};

export default Memberships;
